/**
 * Get the API URL
 * Always use the public API URL as specified
 */
import type {
  Country,
  Region,
  Castle,
  SearchSuggestion,
  BlogPost,
  BlogCategory,
  BlogTag,
  BlogListResponse,
  RecentActivityItem,
} from '../types';

export function getApiUrl(endpoint: string): string {
  // const baseUrl = process.env.API_URL || ''
  // console.info('baseUrl', baseUrl);
  // console.info('`${baseUrl}${endpoint}`', `${baseUrl}${endpoint}`);
  // return `${baseUrl}${endpoint}`
  const isServer = typeof window === 'undefined';
  if (isServer) {
    const internalBase = process.env.INTERNAL_API_URL ?? '';

    // During build, if nginx hostname fails, fall back to localhost
    // This allows builds to work outside Docker
    const isBuildTime =
      process.env.NODE_ENV === 'production' && !process.env.HOSTNAME?.includes('docker');

    if (isBuildTime && internalBase.includes('nginx')) {
      const publicBase = process.env.API_URL ?? '';
      return `${publicBase}${endpoint}`;
    }

    return `${internalBase}${endpoint}`;
  } else {
    const publicBase = process.env.API_URL ?? '';
    return `${publicBase}${endpoint}`;
  }
}
export async function getCountries(): Promise<Country[]> {
  const response = await fetch(getApiUrl('/countries'));
  if (!response.ok) throw new Error('Failed to fetch countries');
  return response.json();
}

export async function getRegions(countryId: number): Promise<Region[]> {
  const response = await fetch(getApiUrl(`/castles/countries/${countryId}/regions`));
  if (!response.ok) throw new Error('Failed to fetch regions');
  return response.json();
}

export interface CastlesResponse {
  castles: Castle[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export async function getCastles(
  params: {
    country?: string;
    countryId?: number;
    region?: string;
    regionId?: number;
    city?: string;
    search?: string;
    period?: string;
    style?: string;
    condition?: string;
    type?: string;
    status?: string;
    featured?: boolean;
    limit?: number;
    page?: number;
  } = {}
): Promise<CastlesResponse> {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, value.toString());
    }
  });

  const response = await fetch(getApiUrl(`/castles?${searchParams.toString()}`));
  if (!response.ok) throw new Error('Failed to fetch castles');
  const data = await response.json();

  return data as CastlesResponse;
}

export async function getSearchSuggestions(
  query: string
): Promise<{ suggestions: SearchSuggestion[] }> {
  if (query.length < 2) return { suggestions: [] };

  const response = await fetch(
    getApiUrl(`/castles/search/suggestions?q=${encodeURIComponent(query)}`)
  );
  if (!response.ok) throw new Error('Failed to fetch suggestions');
  return response.json();
}

export async function getCastle(id: string): Promise<Castle> {
  const response = await fetch(getApiUrl(`/castles/${id}`));
  if (!response.ok) throw new Error('Failed to fetch castle');
  return response.json();
}

export async function getCountry(idOrSlug: string): Promise<Country & { regions: Region[] }> {
  const response = await fetch(getApiUrl(`/countries/${idOrSlug}`));
  if (!response.ok) throw new Error('Failed to fetch country');
  return response.json();
}

export async function getFeaturedCastles(): Promise<Castle[]> {
  const response = await fetch(getApiUrl('/castles/featured'));
  if (!response.ok) throw new Error('Failed to fetch featured castles');
  return response.json();
}

/**
 * Popular/featured castles for page intros (e.g. 404, map, globe).
 * Server-component friendly: cached for an hour, never throws — returns
 * an empty list if the API is unavailable so the surrounding page can
 * still render.
 */
export async function getPopularCastles(limit = 6): Promise<Castle[]> {
  try {
    const response = await fetch(getApiUrl('/castles/featured'), {
      next: { revalidate: 3600 },
    });
    if (!response.ok) return [];
    const castles = await response.json();
    return Array.isArray(castles) ? castles.slice(0, limit) : [];
  } catch {
    return [];
  }
}

export async function getCastleCount(): Promise<number> {
  const response = await fetch(getApiUrl('/castles/count'));
  if (!response.ok) throw new Error('Failed to fetch castle count');
  const data = await response.json();
  return data.count;
}

export async function getFeaturedBlogPosts(limit: number = 3): Promise<BlogPost[]> {
  try {
    const response = await fetch(getApiUrl(`/blog/featured?limit=${limit}`), {
      // Short timeout during build to avoid hanging
      signal: AbortSignal.timeout(3000),
    });

    if (!response.ok) {
      return [];
    }

    return response.json();
  } catch (_error) {
    // Silently fail during build or when API is unavailable
    return [];
  }
}

export async function getRecentActivity(limit: number = 6): Promise<RecentActivityItem[]> {
  try {
    const response = await fetch(getApiUrl(`/recent-activity?limit=${limit}`), {
      // Short timeout during build to avoid hanging
      signal: AbortSignal.timeout(3000),
    });

    if (!response.ok) {
      return [];
    }

    return response.json();
  } catch (_error) {
    // Silently fail during build or when API is unavailable
    return [];
  }
}

export async function getBlogPosts(
  params: {
    page?: number;
    limit?: number;
    category?: string;
    tag?: string;
    search?: string;
  } = {}
): Promise<BlogListResponse> {
  const searchParams = new URLSearchParams();

  if (params.page) searchParams.append('page', params.page.toString());
  if (params.limit) searchParams.append('limit', params.limit.toString());
  if (params.category) searchParams.append('category', params.category);
  if (params.tag) searchParams.append('tag', params.tag);
  if (params.search) searchParams.append('search', params.search);

  const url = `${getApiUrl('/blog')}?${searchParams.toString()}`;

  const response = await fetch(url, {
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch blog posts: ${response.status}`);
  }

  return response.json();
}

// Lets pages 404 only on a real 404; other API failures should be a 500 so crawlers retry
export class ApiNotFoundError extends Error {}

export async function getBlogPost(slug: string, id: number, token?: string): Promise<BlogPost> {
  const response = await fetch(
    getApiUrl(`/blog/${slug}/${id}`) + (token ? `?token=${encodeURIComponent(token)}` : ''),
    {
      cache: 'no-store',
    }
  );

  if (response.status === 404 || response.status === 400) {
    throw new ApiNotFoundError('Blog post not found');
  }
  if (!response.ok) {
    throw new Error('Failed to fetch blog post');
  }

  return response.json();
}

export async function getBlogCategories(): Promise<BlogCategory[]> {
  const response = await fetch(getApiUrl('/blog/categories'), {
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch blog categories');
  }

  return response.json();
}

export async function getBlogTags(): Promise<BlogTag[]> {
  const response = await fetch(getApiUrl('/blog/tags'), {
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch blog tags');
  }

  return response.json();
}

export async function getUserCastles(
  params: { type?: 'favourite' | 'visited'; page?: number; limit?: number } = {}
): Promise<CastlesResponse> {
  const searchParams = new URLSearchParams();
  if (params.type) searchParams.append('type', params.type);
  if (params.page) searchParams.append('page', String(params.page));
  if (params.limit) searchParams.append('limit', String(params.limit));

  const token = getAuthToken();
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(getApiUrl(`/user/castles?${searchParams.toString()}`), {
    headers,
  });

  if (!response.ok) {
    let errText = 'Failed to fetch user castles';
    try {
      const json = await response.json();
      if (json && typeof json.error === 'string') errText = json.error;
      else if (json && typeof json.message === 'string') errText = json.message;
    } catch (_e) {
      if (response.statusText) errText = response.statusText;
    }
    throw new Error(errText);
  }

  return response.json();
}

// ============================================================================
// Authentication API
// ============================================================================

const AUTH_TOKEN_KEY = 'authToken';

export interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  emailVerified: boolean;
  bio?: string | null;
  avatar?: string | null;
}

export interface AuthResponse {
  token: string;
  user: User;
}

/**
 * Get stored auth token.
 * Session-only tokens (stored in sessionStorage) take precedence over
 * remembered tokens (stored in localStorage).
 */
export function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  return sessionStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem(AUTH_TOKEN_KEY);
}

/**
 * Store auth token.
 * @param rememberMe When true the token persists in localStorage across
 * browser restarts; when false it is kept in sessionStorage and is cleared
 * when the tab or browser is closed.
 */
export function setAuthToken(token: string, rememberMe = true): void {
  if (typeof window === 'undefined') return;
  if (rememberMe) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
  } else {
    sessionStorage.setItem(AUTH_TOKEN_KEY, token);
    localStorage.removeItem(AUTH_TOKEN_KEY);
  }
}

/**
 * Remove auth token
 */
export function removeAuthToken(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(AUTH_TOKEN_KEY);
  sessionStorage.removeItem(AUTH_TOKEN_KEY);
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!getAuthToken();
}

/**
 * Register a new user
 */
export async function register(data: {
  email: string;
  password: string;
  name: string;
}): Promise<{ message: string; email: string }> {
  const response = await fetch(getApiUrl('/auth/register'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Registration failed');
  }

  return result;
}

/**
 * Login user
 * @param data.rememberMe When true the token persists across browser restarts;
 * when false it is session-only (cleared when the tab closes).
 */
export async function login(data: {
  email: string;
  password: string;
  rememberMe?: boolean;
}): Promise<AuthResponse> {
  const response = await fetch(getApiUrl('/auth/login'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Login failed');
  }

  // Store token (localStorage when "remember me" is checked, sessionStorage otherwise)
  setAuthToken(result.token, data.rememberMe ?? true);

  return result;
}

/**
 * Logout user
 */
export async function logout(): Promise<void> {
  const token = getAuthToken();

  if (token) {
    try {
      await fetch(getApiUrl('/auth/logout'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      // Ignore errors, just remove token
      console.error('Logout error:', error);
    }
  }

  removeAuthToken();
}

/**
 * Get current user
 */
export async function getCurrentUser(): Promise<User> {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(getApiUrl('/auth/me'), {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      removeAuthToken();
    }
    throw new Error('Failed to fetch user');
  }

  return response.json();
}

/**
 * Verify email with token
 */
export async function verifyEmail(token: string): Promise<AuthResponse> {
  const response = await fetch(getApiUrl(`/auth/verify-email/${token}`));

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Email verification failed');
  }

  // Store token for auto-login
  setAuthToken(result.token);

  return result;
}

/**
 * Resend verification email
 */
export async function resendVerification(email: string): Promise<{ message: string }> {
  const response = await fetch(getApiUrl('/auth/resend-verification'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Failed to resend verification email');
  }

  return result;
}

/**
 * Request password reset
 */
export async function forgotPassword(email: string): Promise<{ message: string }> {
  const response = await fetch(getApiUrl('/auth/forgot-password'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Failed to send password reset email');
  }

  return result;
}

/**
 * Reset password with token
 */
export async function resetPassword(data: {
  token: string;
  password: string;
}): Promise<{ message: string }> {
  const response = await fetch(getApiUrl('/auth/reset-password'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Password reset failed');
  }

  return result;
}

/**
 * Change password (requires authentication)
 */
export async function changePassword(data: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ message: string }> {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(getApiUrl('/auth/change-password'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Password change failed');
  }

  return result;
}

/**
 * Update the logged-in user's public profile (name, bio)
 */
export async function updateProfile(data: {
  name?: string;
  bio?: string | null;
}): Promise<User> {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(getApiUrl('/auth/profile'), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Failed to update profile');
  }

  return result;
}

/**
 * Upload the logged-in user's avatar (multipart/form-data)
 */
export async function uploadAvatar(file: File): Promise<User> {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated');
  }

  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(getApiUrl('/auth/avatar'), {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const result = await response.json().catch(() => ({ error: 'Avatar upload failed' }));

  if (!response.ok) {
    throw new Error(result.error || 'Avatar upload failed');
  }

  return result;
}

// Export as default object for easier imports
export const api = {
  getCountries,
  getRegions,
  getCastles,
  getSearchSuggestions,
  getCastle,
  getCountry,
  getFeaturedCastles,
  getCastleCount,
  getFeaturedBlogPosts,
  getRecentActivity,
  getBlogPosts,
  getBlogPost,
  getBlogCategories,
  getBlogTags,
  // Auth
  register,
  login,
  logout,
  getCurrentUser,
  verifyEmail,
  resendVerification,
  forgotPassword,
  resetPassword,
  changePassword,
  getAuthToken,
  isAuthenticated,
};
